import React, { useEffect, useState } from "react";
import { Share, PlusSquare, Download, X } from "lucide-react";

const DISMISS_KEY = "tir_install_prompt_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

/**
 * InstallAppPrompt — slim dismissible banner inviting the user to install
 * the studio to their home screen.
 * Android/Chrome: captures beforeinstallprompt and triggers the native
 * install dialog. iOS Safari: shows Share → Add to Home Screen steps.
 * Hidden when already installed (standalone) or previously dismissed.
 */
export default function InstallAppPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;
    if (isIos()) {
      setVisible(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => null);
    if (choice?.outcome === "accepted") setVisible(false);
    setDeferred(null);
  };

  return (
    <div
      className="mb-6 flex items-center gap-3 bg-[var(--tir-navy)] text-white rounded-md px-4 py-3"
      data-testid="install-app-prompt"
    >
      <Download size={18} className="shrink-0 opacity-80" />
      <div className="flex-1 min-w-0 text-[12px] leading-snug">
        <span className="font-semibold">Get the app feel — </span>
        {deferred ? (
          <span>install The Intuitive Runner to your home screen.</span>
        ) : isIos() ? (
          <span className="inline-flex items-center gap-1 flex-wrap">
            tap <Share size={13} className="inline -mt-0.5" /> Share, then
            <PlusSquare size={13} className="inline -mt-0.5" /> “Add to Home Screen”.
          </span>
        ) : (
          <span>use your browser menu → “Install app” / “Add to Home screen”.</span>
        )}
      </div>
      {deferred && (
        <button
          type="button"
          onClick={install}
          data-testid="install-app-btn"
          className="shrink-0 text-[10px] tracking-[0.2em] uppercase font-bold bg-white text-[var(--tir-navy)] px-3 py-2 rounded-sm hover:bg-white/90"
        >
          Install
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        data-testid="install-app-dismiss"
        className="shrink-0 text-white/70 hover:text-white p-1"
        aria-label="Dismiss"
      >
        <X size={15} />
      </button>
    </div>
  );
}
